import React, { useState, useEffect, useRef } from 'react'
import { StaticGrid, CopyFieldTemplate } from 'components/common'
import { useSelector, useDispatch } from 'react-redux'
import { selectEpisodeDraftHasContributions, selectEpisodeResultOriginal, selectEpisodeResultPresentation, selectIsContributionRowEditing } from 'components/Body/data-entry/AudioVisual/store/selectors'
import { setEpisodeDraftResultsContributionsAction, clearEpisodeDraftResultsContributionsAction, undoClearEpisodeDraftResultsContributionsAction, contributionStartEditing, contributionStopEditing } from 'components/Body/data-entry/AudioVisual/store/actions'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Badge } from 'primereact/badge'
import { Toast } from 'primereact/toast'
import * as Xlsx from 'xlsx'
import { ExportFormat, ContactTypeIds, ContributionTypeIds, isNonEligibleContributor } from 'modules/Constants'
import { validateContributionRow, findMatchingContributionScenario, calculateAutoPercentages, showInvalidContributionScenarioDialog } from 'components/Body/data-entry/AudioVisual/validations'
import { createToggleDeleteHandler } from 'components/common/CommonGrids/utils/createToggleDeleteHandler'
import { getOrangeBadgeClass } from 'components/common/CommonGrids/utils/badgeUtils'
import { sanitizeExportData } from 'modules/exportSanitizer'
import ContactSearchSelectModal from 'components/Body/data-entry/ContactSearchSelect'
import { isNameSelectable } from 'components/Body/data-entry/ContactSearchSelect/utils/selectionUtils'

export function Contributions(props) {
    const isFilm = props.isFilm
    const excelDownloadName = 'Contributions.xlsx'
    const dispatch = useDispatch()
    const toast = useRef(null)
    const gridRef = useRef()

    // Contact Search Select Modal state
    const [showContactSearchModal, setShowContactSearchModal] = useState(false) 

    const episodeDataOriginal = useSelector(selectEpisodeResultOriginal)
    const episodeData = useSelector(selectEpisodeResultPresentation)
    const { contributionTypes, contributionScenarios, membershipTypes } = useSelector(selectAllAssets)
    const isContributionEditing = useSelector(selectIsContributionRowEditing)

    const contributionsOriginal = episodeDataOriginal && episodeDataOriginal.contributions ? episodeDataOriginal.contributions : []
    let contributions = episodeData && episodeData.contributions ? Object.values(episodeData.contributions) : []

    const hasContributionsModified = useSelector(selectEpisodeDraftHasContributions)
    const setAcceptedContribution = (contribution) => dispatch(setEpisodeDraftResultsContributionsAction(contribution))
    const clearContribution = (id) => dispatch(clearEpisodeDraftResultsContributionsAction(id))
    const undoClearContribution = (id) => dispatch(undoClearEpisodeDraftResultsContributionsAction(id))

    // Generate unique ID for new contribution rows
    const generateContributionId = () => crypto.randomUUID()
    const isContributionsOriginalEmpty = !contributionsOriginal || Object.keys(contributionsOriginal).length === 0

    // Handle toggle delete contribution using shared utility
    const handleToggleDeleteContribution = (rowData) => {
        createToggleDeleteHandler({
            gridRef,
            dataKey: 'id',
            originalData: contributionsOriginal,
            transformOriginal: (originalItem, isDeleted) => ({
                ...originalItem,
                contributionType: contributionTypes?.find(ct => Number(ct.id) === Number(originalItem.contributionTypeId)) || null,
                isDeleted
            }),
            onDelete: clearContribution,
            onUndoDelete: undoClearContribution
        })(rowData)
    }

    // Handle Auto % button click
    const handleAutoPercentageClick = () => {
        // Find matching scenario based on contribution types
        const matchingScenario = findMatchingContributionScenario(contributions, contributionScenarios)

        if (!matchingScenario) {
            showInvalidContributionScenarioDialog()
            return
        }

        // Calculate auto percentages and minutes
        const episodeDuration = episodeData?.durationMinutes || 0
        const { toUpdate, toRemove } = calculateAutoPercentages(contributions, matchingScenario, episodeDuration, contributionsOriginal)

        // Remove contributions that fully match original (no changes at all)
        toRemove.forEach(id => dispatch(undoClearEpisodeDraftResultsContributionsAction(id)))

        // Update contributions that have changes
        if (Object.keys(toUpdate).length > 0) {
            dispatch(setEpisodeDraftResultsContributionsAction(toUpdate))
        }
    }

    // Transform contributions to include contributionType object and name type fields
    const transformContributions = (contribs) => {
        if (!contribs || !contributionTypes) return []
        const transformed = Object.values(contribs).map((c) => {
            // Find the matching contributor name to get nameType info
            const matchingName = c.availableContributorNames?.find(n => n.name === c.contributorName)
            return {
                ...c,
                contributionType: contributionTypes.find(ct => Number(ct.id) === Number(c.contributionTypeId)) || null,
                contributorNameType: c.contributorNameType || matchingName?.nameType || null,
                contributorNameTypeId: c.contributorNameTypeId || matchingName?.nameTypeId || null
            }
        })
        // Sort: new contributions (empty sapCode) appear first
        transformed.sort((a, b) => (a.sapCode ? 1 : 0) - (b.sapCode ? 1 : 0))
        return transformed
    }

    // Build contributor names map from contributions data
    // Note: Backend already filters based on IsSelectable, so we just map the data here
    const contributorNames = React.useMemo(() => {
        if (isContributionsOriginalEmpty) return {}

        const namesMap = {}
        contributions.forEach(contribution => {
            if (contribution.authorKey && contribution.availableContributorNames) {
                namesMap[contribution.authorKey] = contribution.availableContributorNames
            }
        })

        return namesMap
    }, [contributions, isContributionsOriginalEmpty])
   

    const validateData = (rowData) => {
        const errors = validateContributionRow(rowData, episodeData.durationMinutes || 0)
        
        // Additional validation for Non-Eligible Contributors
        if (isNonEligibleContributor(rowData.contactTypeId)) {
            const contributionTypeId = rowData.contributionTypeId || rowData.contributionType?.id
            if (contributionTypeId !== ContributionTypeIds.Actor && contributionTypeId !== ContributionTypeIds.Director) {
                errors.contributionType = 'Contribution Type for a Non-Eligible Contributor must be either Actor or Director'
            }
        }
        
        return errors
    }

    const handleRowEditSave = (rowData) => {
        // Ensure contributionTypeId is synced with contributionType (in case it wasn't updated by the editor)
        if (rowData.contributionType && rowData.contributionType.id) {
            rowData.contributionTypeId = rowData.contributionType.id
        }
        const originalContribution = contributionsOriginal[rowData.id] || Object.values(contributionsOriginal).find(c => c.id === rowData.id)

        // Check if any relevant fields changed from original
        const hasChanges = !originalContribution ||
            originalContribution.contactId !== rowData.contactId ||
            originalContribution.contactNameLineNum !== rowData.contactNameLineNum ||
            originalContribution.contributionTypeId !== rowData.contributionTypeId ||
            originalContribution.minutes !== rowData.minutes ||
            originalContribution.percentage !== rowData.percentage

        if (hasChanges) {
            // Value differs from original - save to draft
            setAcceptedContribution({ [rowData.id]: rowData })
        } else {
            // Value matches original - remove from draft (in case it was previously changed)
            dispatch(undoClearEpisodeDraftResultsContributionsAction(rowData.id))
        }
    }

    const [scrollHeight, setScrollHeight] = useState(calculateScrollHeight())

    //Andy L approves this useEffect
    useEffect(() => {
        const handleResize = () => {
            setScrollHeight(calculateScrollHeight())
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    //TODO: We should not be changing styles in JS. This should be done in CSS. The staticcommonGrid doesnot allow for this to be done in CSS
    function calculateScrollHeight() {
        const adjustedHeight = window.innerHeight / window.devicePixelRatio
        let height = adjustedHeight * 0.2
        if (!isFilm) {
            height = height - 30
        }
        return `${height}px`
    } 
    const displayActions = function (rowData, options) {
        // Hide delete button when row is in edit mode
        const isEditing = options?.isEditing

        return (
            <div id={props.id + '_avList_ContributionGrid_actions'} className="d-flex justify-content-start align-items-center">
                {!isEditing && (
                    <Button
                        className="ml-2"
                        icon={rowData.isDeleted ? "fa-light fa-trash-slash" : "fa-light fa-trash"}
                        rounded
                        onClick={() => handleToggleDeleteContribution(rowData)}
                    />
                )}
            </div>
        )
    }

    const displayAuthorKey = function (rowData) {
        return CopyFieldTemplate(rowData, 'authorKey', props.id + '_contributions_authorKey')
    }

    const displayIpiNo = function (rowData) {
        return CopyFieldTemplate(rowData, 'ipiNo', props.id + '_contributions_ipiNo')
    }

    /**
     * Membership types badge template with orange shading
     * Reuses utility from ContactLanding for consistency
     * Handles comma-separated IDs (e.g., "1,2") from aggregated data
     */
    const displayMembershipTypes = (rowData) => {
        const rawMemberships = rowData.membershipTypes
        if (!rawMemberships) {
            return null
        }
        
        // Normalize to array
        let memberships
        if (Array.isArray(rawMemberships)) {
            memberships = rawMemberships
        } else if (typeof rawMemberships === 'string' && rawMemberships.includes(',')) {
            // Handle comma-separated string (e.g., "1,2" from aggregate)
            memberships = rawMemberships.split(',').map(m => m.trim()).filter(m => m)
        } else {
            memberships = [rawMemberships]
        }
        
        if (memberships.length === 0) {
            return null
        }
        
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                {memberships.map((membership, index) => {
                    let membershipType
                    let membershipName
                    let membershipTypeId
                    
                    // Try to parse as number if it's a string that looks like a number
                    const parsedId = typeof membership === 'string' && !isNaN(membership) 
                        ? parseInt(membership, 10) 
                        : membership
                    
                    if (typeof parsedId === 'number') {
                        // membership is an ID (or was a numeric string)
                        membershipType = membershipTypes?.find(mt => mt.id === parsedId)
                        membershipName = membershipType?.description || `Unknown (${parsedId})`
                        membershipTypeId = parsedId
                    } else {
                        // membership is a name (non-numeric string)
                        membershipType = membershipTypes?.find(mt => mt.description === membership)
                        membershipName = membership
                        membershipTypeId = membershipType?.id || 0
                    }
                    
                    return (
                        <Badge 
                            key={index} 
                            value={membershipName} 
                            className={`custom-badge-base ${getOrangeBadgeClass(membershipTypeId)}`} 
                        />
                    )
                })}
            </div>
        )
    }

    // Handle Add Contributor button click
    const handleAddContributorClick = () => {
        setShowContactSearchModal(true)
    }

    // Handle contributor confirmed from Contact Search Select modal
    const handleContributorConfirmed = (data) => {
        // Transform contact names to availableContributorNames format
        // Only include selectable names (Main Writing, Pseudonym, or Correspondence if only one)
        const availableContributorNames = data.contact.names
            .filter(n => isNameSelectable(n, data.contact.names))
            .map(n => ({
                name: n.name,
                nameType: n.typeSapCode,
                nameTypeId: n.typeId,
                lineNum: n.lineNum
            }))

        // Create initial data for the new row (matches contribution structure)
        const initialData = {
            sapCode: '', // Empty for new contributions
            authorKey: data.contact.authorKey,
            contactId: data.contact.id,
            contactType: data.contact.contactType,
            contactTypeId: data.contact.contactTypeId,
            contributorName: data.name.name,
            contactNameLineNum: data.name.lineNum,
            contributorNameType: data.name.typeSapCode,
            contributorNameTypeId: data.name.typeId,
            ipiNo: data.name.ipiNumber || null,
            membershipLineNum: data.membership?.lineNum ?? null,
            membershipTypes: data.membership ? [data.membership.membershipType] : [],
            availableContributorNames,
            // Fields for user to fill (required by validation)
            contributionType: null,
            contributionTypeId: null,
            percentage: 0,
            minutes: 0,
            // Default values
            isPublicDomain: false,
            isExcluded: false,
            isNew: true
        }

        // Add row with data and open in edit mode first
        if (gridRef.current) {
            gridRef.current.addNewRow(initialData)
        }

        // Close modal after adding row
        setShowContactSearchModal(false)
    }

    // Handle successful creation of potential member
    const handlePotentialMemberCreated = (newContact) => {
        const initialData = {
            sapCode: '',
            authorKey: newContact.authorKey,
            contactId: newContact.aggregateId,
            contributorName: newContact.displayName,
            contactNameLineNum: 2, // MainWriting name (LineNum 2)
            contributorNameType: 'MW',
            contributorNameTypeId: 2,
            ipiNo: null,
            membershipLineNum: null,
            membershipTypes: [],
            availableContributorNames: [{
                name: newContact.displayName,
                nameType: 'MW',
                nameTypeId: 2,
                lineNum: 2
            }],
            contributionType: null,
            contributionTypeId: null,
            percentage: 0,
            minutes: 0,
            isPublicDomain: false,
            isExcluded: false,
            isNew: true
        }

        if (gridRef.current) {
            gridRef.current.addNewRow(initialData)
        }

        setShowContactSearchModal(false)
    }

    const handleExport = (format) => {
        const transformedData = transformContributions(episodeData.contributions)
        const columnMappings = contributionColumnDefinitions
            .filter(col => col.mapsToField)
            .map(col => ({ title: col.title, field: col.mapsToField }))

        const dataToExport = transformedData.map(item =>
            Object.fromEntries(columnMappings.map(({ title, field }) => {
                let value = item[field]
                if (field === 'contributionType') {
                    value = value?.name
                } else if (field === 'minutes' && value !== null && value !== undefined) {
                    value = parseFloat(value.toFixed(2))
                } else if (field === 'membershipTypes') {
                    // Normalize to array and convert IDs to descriptions
                    let memberships
                    if (Array.isArray(value)) {
                        memberships = value
                    } else if (typeof value === 'string' && value.includes(',')) {
                        // Handle comma-separated string (e.g., "1,2" from aggregate)
                        memberships = value.split(',').map(m => m.trim()).filter(m => m)
                    } else {
                        memberships = value ? [value] : []
                    }
                    
                    const membershipNames = memberships.map(membership => {
                        const parsedId = typeof membership === 'string' && !isNaN(membership) 
                            ? parseInt(membership, 10) 
                            : membership
                        
                        if (typeof parsedId === 'number') {
                            const membershipType = membershipTypes?.find(mt => mt.id === parsedId)
                            return membershipType?.description || membership
                        }
                        return membership
                    })
                    value = membershipNames.join(', ')
                }
                return [title, value]
            }))
        )

        const sanitizedData = sanitizeExportData(dataToExport)

        if (format === ExportFormat.Excel) {
            const worksheet = Xlsx.utils.json_to_sheet(sanitizedData)
            const workbook = Xlsx.utils.book_new()
            Xlsx.utils.book_append_sheet(workbook, worksheet, 'Contributions')
            Xlsx.writeFile(workbook, excelDownloadName)
        } else if (format === ExportFormat.Csv) {
            const worksheet = Xlsx.utils.json_to_sheet(sanitizedData)
            const csvOutput = Xlsx.utils.sheet_to_csv(worksheet)
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = excelDownloadName.replace('.xlsx', '.csv') // Ensure filename is CSV
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        }
    }

    const header = (
        <div className="d-flex justify-content-end">
            <Button id={props.id + '_container_contribution_A%'} className="mr-3" label="Auto %" onClick={handleAutoPercentageClick} disabled={isContributionEditing || contributions.length === 0} />
            <Button id={props.id + '_container_contribution_dataTable_export_csv'} className="mr-3" label="CSV Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Csv)} disabled={hasContributionsModified || isContributionsOriginalEmpty} />
            <Button id={props.id + '_container_contribution_dataTable_export_excel'} className="mr-3" label="Excel Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Excel)} disabled={hasContributionsModified || isContributionsOriginalEmpty} />
            <Button id={props.id + '_container_contribution_add'} label="Add Contributor" icon="fa-light fa-plus" onClick={handleAddContributorClick} />
        </div>
    )

    const contributionColumnDefinitions = [
        { columnType: 'text', title: 'Author Key', mapsToField: 'authorKey', filter: false, filterMatchMode: FilterMatchMode.STARTS_WITH, sortable: false, editable: false, template: displayAuthorKey },
        { columnType: 'contributorname', title: 'Contributor Name', mapsToField: 'contributorName', filter: false, filterMatchMode: FilterMatchMode.STARTS_WITH, sortable: false, editable: true },
        { columnType: 'contributiontype', title: 'Contribution', mapsToField: 'contributionType', filter: false, sortable: false, editable: true },
        { columnType: 'contributionpercentage', title: '%', mapsToField: 'percentage', editable: true, min: 0, max: 100 },
        { columnType: 'contributionminutes', title: 'Minutes Worked', mapsToField: 'minutes', filter: false, filterMatchMode: FilterMatchMode.EQUALS, sortable: false, editable: true, min: 0, max: 1000 },
        { columnType: 'text', title: 'IPI No.', mapsToField: 'ipiNo', filter: false, filterMatchMode: FilterMatchMode.EQUALS, sortable: false, editable: false, template: displayIpiNo, className: 'ipi-column' },
        { columnType: 'boolean', title: 'Public Domain', mapsToField: 'isPublicDomain', filter: false, sortable: false, editable: false },
        { columnType: 'boolean', title: 'Excluded', mapsToField: 'isExcluded', filter: false, sortable: false, editable: false },
        { columnType: 'membershiptypes', title: 'Membership Types', mapsToField: 'membershipTypes', filter: false, sortable: false, editable: false, template: displayMembershipTypes },
        { columnType: 'actions', title: 'Actions', template: displayActions }
    ]
    
    return (
        <div id={props.id + '_contributions'}>
            <Toast ref={toast} />
            <ContactSearchSelectModal
                visible={showContactSearchModal}
                onHide={() => setShowContactSearchModal(false)}
                onConfirm={handleContributorConfirmed}
                onPotentialMemberCreated={handlePotentialMemberCreated}
            />
            <StaticGrid
                id={props.id + '_contributions_commonGrid'}
                ref={gridRef}
                episodeId={episodeData.id}
                dataKey="id"
                keyColumnName="id"
                scrollHeight={scrollHeight}
                data={transformContributions(episodeData.contributions)}
                originalData={transformContributions(contributionsOriginal)}
                totalCount={contributions.length}
                emptyMessage="No Contributions"
                columnDefinitions={contributionColumnDefinitions}
                showPaginator={false}
                header={header}
                allowRowEdits={true}
                validateData={validateData}
                onSave={handleRowEditSave}
                draftAvailable={hasContributionsModified}
                contributionTypes={contributionTypes}
                contributorNames={contributorNames}
                episodeDuration={episodeData?.durationMinutes || 0}
                toastRef={toast}
                startEditing={contributionStartEditing}
                stopEditing={contributionStopEditing}
                generateNewRowKey={generateContributionId}
                contactTypeIds={ContactTypeIds}
                contributionTypeIds={ContributionTypeIds}
            />
        </div>
    )
}
